import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { format, startOfMonth, endOfMonth, startOfYear, subMonths, addMonths, subDays, addDays } from 'date-fns';
import { api } from '../../utils/api';
import { useReportData } from '../../hooks/useReportData';

/**
 * Shared helpers for the native report pages (pages/reports/*):
 * period-preset → { start, end } resolution, period labels for
 * subtitles/pills, org location loading, debouncing, and CSV download.
 *
 * All dates are 'YYYY-MM-DD' strings (the /reportData envelope format).
 */

export const toIsoDate = (d) => format(d, 'yyyy-MM-dd');

/** TZ-safe parse of a 'YYYY-MM-DD' (or ISO datetime) string into a local Date. */
export function parseIsoDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  const [datePart] = String(value).split('T');
  const [y, m, d] = datePart.split('-').map(Number);
  if (!y || !m) return null;
  return new Date(y, m - 1, d || 1);
}

/** Same { start, end } range one year earlier (STLY — same time last year). */
export function stlyRange(range) {
  const start = parseIsoDate(range?.start);
  const end = parseIsoDate(range?.end);
  if (!start || !end) return null;
  start.setFullYear(start.getFullYear() - 1);
  end.setFullYear(end.getFullYear() - 1);
  return { start: toIsoDate(start), end: toIsoDate(end) };
}

/**
 * Resolve a period-preset key to { start, end } ISO date strings.
 * Returns null for unknown keys (e.g. 'custom' — caller supplies the range).
 *
 * Keys: 'mtd' | 'last3' (MTD last 3 months) | 'last12'/'trailing12' | 'ytd' |
 *       'thisMonth' | 'lastMonth' | 'thisYear' |
 *       'last7' | 'last30' (full days ending yesterday — operations pages)
 */
export function resolvePeriodRange(presetKey, today = new Date()) {
  switch (presetKey) {
    case 'mtd':
      return { start: toIsoDate(startOfMonth(today)), end: toIsoDate(today) };
    case 'last7': {
      const end = subDays(today, 1);
      return { start: toIsoDate(subDays(end, 6)), end: toIsoDate(end) };
    }
    case 'last30': {
      const end = subDays(today, 1);
      return { start: toIsoDate(subDays(end, 29)), end: toIsoDate(end) };
    }
    case 'last3':
      // PBI "MTD Last 3 Months": 3 full prior months + current month (budgets span the full month)
      return { start: toIsoDate(startOfMonth(subMonths(today, 3))), end: toIsoDate(endOfMonth(today)) };
    case 'last12':
      return { start: toIsoDate(startOfMonth(subMonths(today, 11))), end: toIsoDate(today) };
    // The financial pages' key — calendar-close orgs (financialCloseDay < 0)
    // resolve it here via resolveFinancialPeriodRange's delegation, so it must
    // resolve (a null range renders an empty date picker). Trailing 12 NEVER
    // includes the in-progress month: 12 FULL months ending at the most recent
    // completed month. Delayed-close orgs never reach here — the close-aware
    // switch anchors them at the last closed month per financialCloseDay.
    case 'trailing12': {
      const closed = subMonths(today, 1);
      return { start: toIsoDate(startOfMonth(subMonths(closed, 11))), end: toIsoDate(endOfMonth(closed)) };
    }
    case 'ytd':
      return { start: toIsoDate(startOfYear(today)), end: toIsoDate(today) };
    case 'thisMonth':
      return { start: toIsoDate(startOfMonth(today)), end: toIsoDate(endOfMonth(today)) };
    case 'lastMonth': {
      const prev = subMonths(today, 1);
      return { start: toIsoDate(startOfMonth(prev)), end: toIsoDate(endOfMonth(prev)) };
    }
    case 'thisYear':
      return { start: toIsoDate(startOfYear(today)), end: toIsoDate(today) };
    case 'yesterday': {
      const y = subDays(today, 1);
      return { start: toIsoDate(y), end: toIsoDate(y) };
    }
    case 'nextMonth': {
      const next = addDays(endOfMonth(today), 1);
      return { start: toIsoDate(next), end: toIsoDate(endOfMonth(next)) };
    }
    case 'next30':
      return { start: toIsoDate(today), end: toIsoDate(addDays(today, 29)) };
    case 'next90':
      return { start: toIsoDate(today), end: toIsoDate(addDays(today, 89)) };
    default:
      return null;
  }
}

/**
 * True when the org opts out of close-aware anchoring: financialCloseDay = -1
 * (any negative) means month presets resolve as TRUE CALENDAR dates — "last
 * month" is simply the previous calendar month. Any positive day keeps the
 * delayed-close behavior.
 */
export const isCalendarCloseMode = (closeDay) => Number(closeDay) < 0;

/**
 * Latest CLOSED month under the org's financial close day: month M's books
 * close on day `closeDay` of month M+1 (same convention as the backend's
 * getLatestClosedMonth in forecastCalculatorV3/ContextBuilder). Example:
 * closeDay=15 → on July 1 the latest closed month is May; June closes Jul 15.
 * closeDay=-1 → calendar mode: the previous calendar month is always
 * "closed". Returns a Date pinned to the first of the closed month.
 */
export function lastClosedMonth(closeDay = 15, today = new Date()) {
  if (isCalendarCloseMode(closeDay)) return startOfMonth(subMonths(today, 1));
  const day = Number.isFinite(Number(closeDay)) && Number(closeDay) >= 1 ? Number(closeDay) : 15;
  let cursor = startOfMonth(subMonths(today, 1));
  for (let i = 0; i < 24; i++) {
    const closeDate = addDays(startOfMonth(addMonths(cursor, 1)), day - 1);
    if (today >= closeDate) return cursor;
    cursor = startOfMonth(subMonths(cursor, 1));
  }
  return cursor;
}

/**
 * Close-aware period presets for the FINANCIAL pages: month-grain quick
 * options anchor on the latest CLOSED month so in-progress (accrual-polluted)
 * months never show up unless explicitly chosen (MTD / Custom).
 *
 *   lastMonth   → the last closed month
 *   last3       → 3 closed months ending at the last closed month
 *   trailing12  → 12 closed months ending at the last closed month
 *   ytd         → Jan 1 of the closed month's year → end of closed month
 *   anything else falls through to resolvePeriodRange (mtd stays literal —
 *   picking it is the explicit "show me the open month" gesture)
 *
 * financialCloseDay = -1 disables the anchoring entirely: every preset
 * resolves as plain calendar dates.
 */
export function resolveFinancialPeriodRange(presetKey, closeDay = 15, today = new Date()) {
  if (isCalendarCloseMode(closeDay)) return resolvePeriodRange(presetKey, today);
  const closed = lastClosedMonth(closeDay, today);
  switch (presetKey) {
    case 'lastMonth':
      return { start: toIsoDate(closed), end: toIsoDate(endOfMonth(closed)) };
    case 'last3':
      return { start: toIsoDate(startOfMonth(subMonths(closed, 2))), end: toIsoDate(endOfMonth(closed)) };
    case 'last12':
    case 'trailing12':
      return { start: toIsoDate(startOfMonth(subMonths(closed, 11))), end: toIsoDate(endOfMonth(closed)) };
    case 'ytd':
      return { start: toIsoDate(startOfYear(closed)), end: toIsoDate(endOfMonth(closed)) };
    default:
      return resolvePeriodRange(presetKey, today);
  }
}

/** "March – June 2026" / "Mar 2025 – Jun 2026" / "June 2026" (month grain). */
export function formatMonthRangeLabel(startIso, endIso) {
  const s = parseIsoDate(startIso);
  const e = parseIsoDate(endIso);
  if (!s || !e) return '';
  if (s.getFullYear() === e.getFullYear()) {
    if (s.getMonth() === e.getMonth()) return format(e, 'MMMM yyyy');
    return `${format(s, 'MMMM')} – ${format(e, 'MMMM yyyy')}`;
  }
  return `${format(s, 'MMM yyyy')} – ${format(e, 'MMM yyyy')}`;
}

/** "January 1 – June 10, 2026" / "Dec 28, 2025 – Jan 4, 2026" (day grain). */
export function formatDateRangeLabel(startIso, endIso) {
  const s = parseIsoDate(startIso);
  const e = parseIsoDate(endIso);
  if (!s || !e) return '';
  if (s.getFullYear() === e.getFullYear()) {
    return `${format(s, 'MMMM d')} – ${format(e, 'MMMM d, yyyy')}`;
  }
  return `${format(s, 'MMM d, yyyy')} – ${format(e, 'MMM d, yyyy')}`;
}

/** Compact x-axis tick for a backend period string at a given grain. */
export function formatPeriodTick(periodIso, granularity = 'month') {
  const d = parseIsoDate(periodIso);
  if (!d) return String(periodIso ?? '');
  if (granularity === 'day' || granularity === 'd') return format(d, 'M/d');
  if (granularity === 'year' || granularity === 'y') return format(d, 'yyyy');
  return format(d, 'MMM yy');
}

/**
 * "24 properties · March – June 2026" subtitle line. The org name parameter
 * is accepted (call sites still pass it) but intentionally not rendered —
 * the viewer knows whose portfolio they're in, and the compact header band
 * has no room for ceremony.
 */
export function buildReportSubtitle(orgName, propertyCount, periodLabel) {
  const parts = [];
  if (propertyCount != null) parts.push(`${propertyCount} ${propertyCount === 1 ? 'property' : 'properties'}`);
  if (periodLabel) parts.push(periodLabel);
  return parts.join(' · ');
}

/**
 * Envelope locationIds assembly: undefined when nothing (or everything)
 * is selected — the server then applies the user's full access list.
 */
export function buildLocationIdsParam(selectedIds, allLocations = []) {
  if (!Array.isArray(selectedIds) || selectedIds.length === 0) return undefined;
  if (allLocations.length > 0 && selectedIds.length >= allLocations.length) return undefined;
  return selectedIds.map(Number).filter(Number.isFinite);
}

/**
 * useOrgLocations(organizationId) — GET /locations?orgId=… via the shared
 * axios instance (same source DashboardNew uses for location names).
 * Returns { locations: [{ id, name, currency, … }], isLoading, error }.
 */
export function useOrgLocations(organizationId) {
  const [state, setState] = useState({ locations: [], isLoading: false, error: null });

  useEffect(() => {
    if (!organizationId) {
      setState({ locations: [], isLoading: false, error: null });
      return undefined;
    }
    let cancelled = false;
    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    api
      .get(`/locations?orgId=${organizationId}`)
      .then((res) => {
        if (cancelled) return;
        const locations = Array.isArray(res.data) ? res.data : [];
        setState({ locations, isLoading: false, error: null });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({ locations: [], isLoading: false, error: err });
      });
    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  return state;
}

/** Debounce a fast-changing value (search inputs → filters.search). */
export function useDebouncedValue(value, delayMs = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

/**
 * Remember the last non-empty option list so filter chips keep their full
 * option set while a filtered refetch narrows the underlying rows.
 */
export function useStickyOptions(options) {
  const ref = useRef([]);
  if (Array.isArray(options) && options.length > ref.current.length) {
    ref.current = options;
  }
  return ref.current.length ? ref.current : options || [];
}

const titleCaseFacet = (s) =>
  String(s || '')
    .toLowerCase()
    .replace(/\b([a-z])/g, (m, c) => c.toUpperCase());

/**
 * Account / Vendor / Account-Type filter options for the Financial report pages,
 * fetched once per (org + date-range + location) scope from the `financialFacets`
 * endpoint. Unlike row-derived option lists, these reflect the WHOLE scope, not
 * just the current page — and the fetch deliberately omits page/sort and the
 * account/vendor selections so applying a filter never shrinks the option set.
 *
 * `pnlScope` restricts accounts to P&L account types (P&L / Trends pages); GL
 * omits it to list every account type. Account option labels embed both number
 * and name ("4010 · Room Revenue") so the FilterChip search matches either.
 *
 * @returns {{ accountOptions, vendorOptions, accountTypeOptions, isLoading }}
 */
export function useFinancialFacets({
  organizationId,
  locationIds,
  startDate,
  endDate,
  pnlScope = false,
  statistics = false,
  accountTypes = null,
  enabled = true,
}) {
  // Scope precedence mirrors fetchFinancialFacets: statistics → stat-account
  // universe; explicit accountTypes (Revenue/Expense metrics) → those types;
  // pnlScope → all P&L types; otherwise unscoped (GL Detail).
  const scopeFilters = statistics
    ? { filters: { statistics: true } }
    : Array.isArray(accountTypes) && accountTypes.length
      ? { filters: { metricAccountTypes: accountTypes } }
      : pnlScope
        ? { filters: { pnlScope: true } }
        : {};
  // No useMemo needed: useReportData keys its react-query cache on a stable
  // stringify of params, so a fresh object with identical scope still hits cache.
  const facetParams =
    organizationId && startDate && endDate
      ? {
          organizationId,
          ...(Array.isArray(locationIds) && locationIds.length ? { locationIds } : {}),
          startDate,
          endDate,
          ...scopeFilters,
        }
      : null;

  const { data, isLoading } = useReportData('financialFacets', facetParams, { enabled });

  const accountOptions = useMemo(
    () =>
      (data?.accounts || []).map((a) => ({
        value: a.chartOfAccountId,
        label:
          [a.accountNumber, a.accountName].filter(Boolean).join(' · ') || `Account ${a.chartOfAccountId}`,
      })),
    [data]
  );
  // Deduped picker entries stand for one account per location — expand a
  // selection back to every underlying chartOfAccountId before it goes into
  // filters.accountIds. Unknown values (old share links carrying raw ids)
  // pass through unchanged.
  const accountIdMap = useMemo(() => {
    const map = new Map();
    (data?.accounts || []).forEach((a) => {
      map.set(a.chartOfAccountId, Array.isArray(a.ids) && a.ids.length ? a.ids : [a.chartOfAccountId]);
    });
    return map;
  }, [data]);
  const expandAccountIds = useCallback(
    (selected) => {
      const list = Array.isArray(selected) ? selected : selected == null || selected === '' ? [] : [selected];
      return list.flatMap((v) => accountIdMap.get(v) || [v]);
    },
    [accountIdMap]
  );
  const vendorOptions = useMemo(() => (data?.vendors || []).map((v) => ({ value: v, label: v })), [data]);
  const accountTypeOptions = useMemo(
    () => (data?.accountTypes || []).map((t) => ({ value: t, label: titleCaseFacet(t) })),
    [data]
  );
  const departmentOptions = useMemo(
    () => (data?.departments || []).map((d) => ({ value: d, label: d })),
    [data]
  );
  // Whole-statement Account-type options: monetary P&L types + inferred
  // statistical types (kind: 'monetary' | 'stat') — see fetchFinancialFacets.
  const statementTypeOptions = useMemo(
    () =>
      (data?.statementTypes || []).map((t) => ({
        value: t.accountType,
        label: titleCaseFacet(t.accountType),
        kind: t.kind,
      })),
    [data]
  );

  return { accountOptions, expandAccountIds, vendorOptions, accountTypeOptions, departmentOptions, statementTypeOptions, isLoading };
}

/** "Jun 9, 2026" (detail-table dates). */
export function formatDateLong(dateIso) {
  const d = parseIsoDate(dateIso);
  return d ? format(d, 'MMM d, yyyy') : '—';
}

/** "June 2026" (matrix month group headers). */
export function formatMonthLong(dateIso) {
  const d = parseIsoDate(dateIso);
  return d ? format(d, 'MMMM yyyy') : '';
}

/** Every 'YYYY-MM-DD' between start and end inclusive (capped at maxDays). */
export function enumerateDays(startIso, endIso, maxDays = 62) {
  const s = parseIsoDate(startIso);
  const e = parseIsoDate(endIso);
  if (!s || !e || s > e) return [];
  const out = [];
  for (let d = s; d <= e && out.length < maxDays; d = addDays(d, 1)) out.push(toIsoDate(d));
  return out;
}

const csvEscape = (v) => {
  const s = v === null || v === undefined ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/** Flat rows → CSV string. columns: [{ key, label }]. */
export function rowsToCsv(columns, rows) {
  const lines = [columns.map((c) => csvEscape(c.label)).join(',')];
  (rows || []).forEach((row) => {
    lines.push(columns.map((c) => csvEscape(row[c.key])).join(','));
  });
  return lines.join('\n');
}

/* ------------------------------------------------------------------ */
/* Revenue-page additions (RevenueOverview/Trends/Projections/Forecast */
/* + NightAudits)                                                       */
/* ------------------------------------------------------------------ */

/** "6/1/2026 – 6/10/2026" for the read-only Date filter chip. */
export function formatDateChip(range) {
  const s = parseIsoDate(range?.start);
  const e = parseIsoDate(range?.end);
  if (!s || !e) return '—';
  return `${format(s, 'M/d/yyyy')} – ${format(e, 'M/d/yyyy')}`;
}

/**
 * Short human period text: full month → "May 2026", same-month partial →
 * "Jun 1 – 10, 2026", cross-month → "Jun 4 – Jul 10, 2026".
 */
export function describeRange(range) {
  const s = parseIsoDate(range?.start);
  const e = parseIsoDate(range?.end);
  if (!s || !e) return '';
  const sameMonth = s.getFullYear() === e.getFullYear() && s.getMonth() === e.getMonth();
  if (sameMonth) {
    if (s.getDate() === 1 && toIsoDate(e) === toIsoDate(endOfMonth(e))) return format(s, 'MMMM yyyy');
    return `${format(s, 'MMM d')} – ${format(e, 'd, yyyy')}`;
  }
  if (s.getFullYear() === e.getFullYear()) return `${format(s, 'MMM d')} – ${format(e, 'MMM d, yyyy')}`;
  return `${format(s, 'MMM d, yyyy')} – ${format(e, 'MMM d, yyyy')}`;
}

/**
 * The /reportData route layer is expected to return the page derivation as
 * `data`, but utils/reportDataSql.fetchRevenuePages wraps it as
 * { pageKind, source, currency, rowCount, data }. Accept either shape.
 */
export function unwrapReportData(data) {
  if (!data) return null;
  if (data.data && (data.pageKind !== undefined || data.source !== undefined)) return data.data;
  return data;
}

/** Normalize any value to an ISO-4217 code, or null if not a valid 3-letter code. */
export function normalizeCurrencyCode(value) {
  if (!value || typeof value !== 'string') return null;
  const upper = value.trim().toUpperCase();
  return /^[A-Z]{3}$/.test(upper) ? upper : null;
}

/**
 * The currency a report should default to before any explicit Currency filter:
 *   • single-location user  → that location's currency (org default if unset)
 *   • multi-location user    → the org's reporting currency
 * `locations` is the RLS-scoped list from useOrgLocations (already limited to
 * the user's accessible locations), so `length === 1` identifies a single-
 * location user. Everything collapses to 'USD' when nothing is configured.
 */
export function resolveDefaultCurrency(locations, orgReportingCurrency) {
  const org = normalizeCurrencyCode(orgReportingCurrency) || 'USD';
  if (Array.isArray(locations) && locations.length === 1) {
    return normalizeCurrencyCode(locations[0]?.currency) || org;
  }
  return org;
}

/** Best-effort ISO currency code from response metadata / payload. */
export function resolveCurrency(metadata, rawData, fallback = 'USD') {
  return (
    metadata?.currency?.targetCurrency ||
    metadata?.targetCurrency ||
    rawData?.currency?.targetCurrency ||
    fallback
  );
}

/**
 * reportMetrics.js ratio outputs (occPct, mtdPctToBudget, pctToBudget) are
 * 0–1; the kit formatters expect percent units. Null-safe ×100.
 */
export function ratioToPct(ratio) {
  if (ratio === null || ratio === undefined || !Number.isFinite(Number(ratio))) return null;
  return Number(ratio) * 100;
}

/** Treat a 0 budget-ish amount as "no budget" → null (renders "—"). */
export function zeroAsNull(value) {
  if (value === null || value === undefined) return null;
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return null;
  return n;
}

/**
 * Pace dot tone vs the portfolio benchmark (revenue-overview.html legend):
 * ≥120% of pace → success, ≥85% → warning (near pace), else error.
 * Null when either operand is missing.
 */
export function paceTone(value, benchmark) {
  const v = Number(value);
  const b = Number(benchmark);
  if (!Number.isFinite(v) || !Number.isFinite(b) || b === 0) return null;
  const ratio = v / b;
  if (ratio >= 1.2) return 'success';
  if (ratio >= 0.85) return 'warning';
  return 'error';
}

/** Day-grain x-axis tick per the revenue mocks ("Jun 01"). */
export function formatDayTick(dateIso) {
  const d = parseIsoDate(dateIso);
  return d ? format(d, 'MMM dd') : String(dateIso ?? '');
}

/** 'Week 1'..'Week 5' bucket label for a date within its month. */
export function weekOfMonthLabel(dateIso) {
  const d = parseIsoDate(dateIso);
  if (!d) return null;
  return `Week ${Math.floor((d.getDate() - 1) / 7) + 1}`;
}

/** Readable filename from a blob/file URL tail (strips query, decodes). */
export function fileNameFromUrl(url) {
  if (!url) return null;
  try {
    const path = String(url).split('?')[0];
    const tail = path.split('/').filter(Boolean).pop();
    return tail ? decodeURIComponent(tail) : null;
  } catch (e) {
    return String(url);
  }
}

/** Trigger a client-side CSV download. */
export function downloadCsv(filename, csvString) {
  const blob = new Blob([`﻿${csvString}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Excel export: convert the same CSV text a page already builds into a real
 * .xlsx workbook (numbers stay numeric). Shell "Export" buttons use this so
 * Excel exports always match the rendered table + current filters exactly.
 */
export function downloadExcelFromCsv(filename, csvString, sheetName = 'Report') {
  // eslint-disable-next-line global-require
  const XLSX = require('xlsx');
  const wb = XLSX.read(csvString, { type: 'string', raw: false });
  wb.SheetNames[0] = sheetName.substring(0, 31) || 'Report';
  wb.Sheets[wb.SheetNames[0]] = wb.Sheets.Sheet1 || wb.Sheets[Object.keys(wb.Sheets)[0]];
  const base = filename.replace(/\.csv$/i, '');
  XLSX.writeFile(wb, base.endsWith('.xlsx') ? base : `${base}.xlsx`);
}

/**
 * CSV export from an array-of-arrays (header row first). Cells escape like
 * matrixToCsv; numbers emit bare so spreadsheets parse them numerically.
 */
export function downloadCsvFromAoa(filename, aoa) {
  const esc = (v) => {
    const s = v === null || v === undefined ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = (aoa || []).map((row) => row.map(esc).join(',')).join('\n');
  downloadCsv(filename.endsWith('.csv') ? filename : `${filename}.csv`, csv);
}

/**
 * Snapshot the on-screen matrix for the PDF export: the current view's row
 * tree (FULL — collapse state ignored, every property/level included) with
 * only the user's visible columns, in their order. The server typesets this
 * exactly, so the PDF is what the user is looking at, expanded.
 */
export function buildClientMatrix(matrix, columnPrefs, { title, rowHeaderLabel } = {}) {
  if (!matrix || !Array.isArray(matrix.rows) || matrix.rows.length === 0) return undefined;
  const byKey = new Map(matrix.columns.map((c) => [c.key, c]));
  const visible = columnPrefs?.visibleKeys?.length
    ? columnPrefs.visibleKeys.map((k) => byKey.get(k)).filter(Boolean)
    : matrix.columns;

  const parents = new Map(matrix.rows.map((r) => [r.id, r.parentId]));
  const levels = new Map();
  const levelOf = (id) => {
    if (levels.has(id)) return levels.get(id);
    const p = parents.get(id);
    const lvl = p === null || p === undefined ? 0 : levelOf(p) + 1;
    levels.set(id, lvl);
    return lvl;
  };

  return {
    title,
    rowHeaderLabel,
    columns: visible.map((c) => ({
      key: c.key,
      label: c.label,
      group: c.group || '',
      format: c.format || 'number',
      variant: c.variant || null,
    })),
    rows: matrix.rows
      .filter((r) => !r.fullRender)
      .map((r) => ({
        label: r.label,
        level: levelOf(r.id),
        variant: r.variant || null,
        // Per-row format override (e.g. the Statistics "Occupancy %" row) —
        // the PDF typesetter prefers it over the column format.
        ...(r.format ? { format: r.format } : {}),
        cells: Object.fromEntries(visible.map((c) => [c.key, r.cells ? (r.cells[c.key] ?? null) : null])),
      })),
    footer: matrix.footer ? { label: matrix.footer.label, cells: matrix.footer.cells } : null,
  };
}
